import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

import { UseMutateFunction } from "@tanstack/react-query";

import {
  useAddImportDatasetMutation,
  useImportDatasetMutation,
  useReplaceImportDatasetMutation
} from "@/api/dataset/useDatasetAPI";
import { FilePlusIcon, InformationIcon, SpinnerIcon } from "@/assets/icons";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { MESSAGE } from "@/constants/message";
import { useDatadogAnalytics } from "@/hooks/tracker/useDatadogAnalytics";
import useToast, { EToastType } from "@/hooks/ui/useToast";

import NotificationContent from "../notification/notificationContent";
import { Alert, AlertDescription } from "../ui/alert";

export enum EUploadDatasetType {
  NEW = "new",
  ADD_MORE_WITH_DUPLICATE = "addmorewithduplicate",
  ADD_MORE = "addmore",
  REPLACE_ALL = "replace",
  DELETE = "delete"
}

export type UploadDatasetDialogType =
  | EUploadDatasetType.NEW
  | EUploadDatasetType.ADD_MORE
  | EUploadDatasetType.REPLACE_ALL
  | EUploadDatasetType.DELETE
  | EUploadDatasetType.ADD_MORE_WITH_DUPLICATE;

const UPLOAD_ERROR_MESSAGE = {
  [EUploadDatasetType.NEW]:
    "Sorry, we’re experiencing an issue while processing your request. Please try again.",
  [EUploadDatasetType.ADD_MORE]:
    "Sorry, we’re experiencing an issue adding data to your dataset. Please try again.",
  [EUploadDatasetType.REPLACE_ALL]:
    "Sorry, we’re experiencing an issue replacing data in your dataset. Please try again.",
  [EUploadDatasetType.DELETE]:
    "Sorry, we’re experiencing an issue deleting your dataset. Please try again.",
  [EUploadDatasetType.ADD_MORE_WITH_DUPLICATE]:
    "Sorry, we’re experiencing an issue adding data with duplicates to your dataset. Please try again."
};

const title = {
  [EUploadDatasetType.ADD_MORE]: "Add More Data",
  [EUploadDatasetType.REPLACE_ALL]: "Replace Data",
  [EUploadDatasetType.NEW]: "Upload Dataset",
  [EUploadDatasetType.DELETE]: "Delete Dataset",
  [EUploadDatasetType.ADD_MORE_WITH_DUPLICATE]:
    "Add More Data (Allow Duplicates)"
};

export interface UploadDialogProps {
  uploadType?: UploadDatasetDialogType;
  setIsLoading?: (loading: boolean) => void;
  setOperationId: (id: string) => void;
  setIsParsingActive: (active: boolean) => void;
  onUploadSuccess?: (uploadType: UploadDatasetDialogType) => void;
  datasetId?: string;
}

export default function UploadDialog({
  uploadType = EUploadDatasetType.NEW,
  setIsLoading,
  setOperationId,
  setIsParsingActive,
  onUploadSuccess,
  datasetId
}: Readonly<UploadDialogProps>) {
  const { trackAction } = useDatadogAnalytics();
  const { showToast } = useToast();

  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  let useImportDataset;

  switch (uploadType) {
    case EUploadDatasetType.NEW:
      useImportDataset = useImportDatasetMutation;
      break;
    case EUploadDatasetType.ADD_MORE:
      useImportDataset = useAddImportDatasetMutation;
      break;
    case EUploadDatasetType.REPLACE_ALL:
      useImportDataset = useReplaceImportDatasetMutation;
      break;
    default:
      useImportDataset = useImportDatasetMutation;
  }

  const uploadMutation = useImportDataset({
    onMutate: () => {
      setIsUploading(true);
    },
    onSuccess: (data, payload) => {
      trackAction("dataset_upload", {
        upload_type: uploadType,
        file_size: (payload.file.get("file") as File).size
      });
      setAbortController(null);
      setOperationId(data.operation_id);
      setIsParsingActive(true);
      setIsUploading(false);
      onUploadSuccess?.(uploadType);
    },
    onError: () => {
      showToast({
        type: EToastType.ERROR,
        message: (
          <div className="flex flex-col gap-1">
            <p className="text-sm font-bold text-txtHeading">
              Something Went Wrong
            </p>
            <p className="text-sm text-txtHeading">
              {UPLOAD_ERROR_MESSAGE[uploadType]}
            </p>
          </div>
        ),
        autoClose: 2000
      });

      setIsParsingActive(false);
      setIsUploading(false);
      setAbortController(null);
    }
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx"
      ]
    },
    maxSize: 100 * 1024 * 1024, // 100MB
    onDropRejected: (fileRejections) => {
      fileRejections.forEach((rejection) => {
        const { errors } = rejection;
        errors.forEach((error) => {
          if (error.code === "file-too-large") {
            showToast({
              type: EToastType.ERROR,
              message: (
                <NotificationContent
                  title={MESSAGE.DATASET_UPLOAD_ERROR_FILE_TOO_LARGE_TITLE}
                  description={
                    MESSAGE.DATASET_UPLOAD_ERROR_FILE_TOO_LARGE_DESCRIPTION
                  }
                />
              ),
              autoClose: 2000
            });
          }
          if (error.code === "file-invalid-type") {
            showToast({
              type: EToastType.ERROR,
              message: (
                <NotificationContent
                  title={MESSAGE.DATASET_UPLOAD_ERROR_FILE_NOT_SUPPORTED_TITLE}
                  description={
                    MESSAGE.DATASET_UPLOAD_ERROR_FILE_NOT_SUPPORTED_DESCRIPTION
                  }
                />
              ),
              autoClose: 2000
            });
          }
        });
      });
    },
    onDropAccepted: (acceptedFiles) => {
      if (!navigator.onLine) {
        showToast({
          type: EToastType.ERROR,
          message: (
            <NotificationContent
              title={MESSAGE.NO_INTERNET_CONNECTION_TITLE}
              description={MESSAGE.NO_INTERNET_CONNECTION_DESCRIPTION}
            />
          ),
          autoClose: 2000
        });
        return;
      }
      const file = acceptedFiles[0];
      if (file) {
        const controller = new AbortController();
        setAbortController(controller);

        const formData = new FormData();
        formData.append("file", file);
        const { mutate } = uploadMutation;

        (
          mutate as UseMutateFunction<
            { operation_id: string },
            Error,
            | { file: FormData; signal?: AbortSignal }
            | { file: FormData; signal?: AbortSignal; id: string }
          >
        )(
          uploadType === EUploadDatasetType.NEW
            ? { file: formData, signal: controller.signal }
            : { file: formData, signal: controller.signal, id: datasetId! }
        );
      }
    }
  });

  const handleCancelUpload = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }

    setIsUploading(false);
    uploadMutation.reset();
  };

  // Render upload progress UI
  const renderUploadProgress = () => {
    return (
      <div className="border-2 border-transparent rounded-lg p-8 text-center transition-colors min-h-[200px] flex flex-col items-center justify-center">
        <SpinnerIcon
          className="spin-animation"
          data-testid="upload-progress-spinner"
          width={60}
          height={60}
        />

        <button
          onClick={handleCancelUpload}
          data-testid="cancel-upload-button"
          className="mt-3 text-royal-blue text-[10px] font-semibold"
        >
          Cancel
        </button>
      </div>
    );
  };

  useEffect(() => {
    if (setIsLoading) setIsLoading(isUploading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading]);

  // Effect to cancel upload on component unmount
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  return (
    <DialogContent data-testid="upload-dataset-modal">
      <DialogHeader className="space-y-1 text-left">
        <DialogTitle className="text-txtHeading text-[20px] font-semibold text-left">
          {title[uploadType]}
        </DialogTitle>
        <DialogDescription className="text-txtBody text-[10px]">
          {(uploadType === EUploadDatasetType.ADD_MORE ||
            uploadType === EUploadDatasetType.REPLACE_ALL) && (
            <Alert className="flex flex-row">
              <InformationIcon
                className="fill-royal-blue"
                width={20}
                height={20}
              />
              <AlertDescription className="!translate-y-0">
                {uploadType === EUploadDatasetType.ADD_MORE
                  ? "You can only add data with the same data structure."
                  : "You can replace all existing data with a new dataset. The new dataset must have the same data structure."}
              </AlertDescription>
            </Alert>
          )}
        </DialogDescription>
      </DialogHeader>

      {isUploading ? (
        renderUploadProgress()
      ) : (
        <div
          {...getRootProps()}
          className="border-2 border-dashed border-[#888888] rounded-lg p-8 text-center cursor-pointer hover:border-[#4C7DFB] transition-colors"
          style={{
            minHeight: "200px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <input {...getInputProps()} data-testid="upload-input" />
          <div className="mb-2">
            <FilePlusIcon className="w-8 h-8 fill-light-gray" />
          </div>
          <p className="text-[16px] font-semibold mb-2">
            <span className="text-[#4C7DFB]">Choose File</span>
            <span className="text-[#616161]"> or Drag and Drop</span>
          </p>
          <p className="text-[10px] text-[#888888] font-normal">
            Upload CSV or XLSX files (max. 100 MB).
          </p>
        </div>
      )}
    </DialogContent>
  );
}
